package com.tovtalk.data.local

import com.tovtalk.domain.entity.Post
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

/**
 * In-memory cache for the discover feed.
 * - Full first page + hero post are cached to render instantly on cold start.
 *
 * TTL: the cached posts carry S3 URLs that the backend signs for ~300 seconds. After
 * background/resume those URLs expire and the media 403s (broken first reel / black hero).
 * We therefore expire the cache well before the signature lifetime ([TTL_MS]) so a stale
 * read returns null and the feed re-fetches freshly-signed URLs instead of rendering
 * dead media.
 */
@OptIn(ExperimentalTime::class)
object DiscoverCache {
    /** Safe TTL: comfortably below the backend's ~300s S3 signature lifetime. */
    private const val TTL_MS = 4 * 60 * 1000L

    private var cachedItems: List<Post>? = null

    /** Single post to display as placeholder while the feed is loading. */
    private var heroPost: Post? = null

    /** Epoch millis when the current cache entry was stored; null when empty. */
    private var storedAtMs: Long? = null

    private fun isExpired(): Boolean {
        val ts = storedAtMs ?: return true
        return (Clock.System.now().toEpochMilliseconds() - ts) >= TTL_MS
    }

    /** Return cached first page, or null if empty or past [TTL_MS]. */
    fun getFirstPage(): List<Post>? = if (isExpired()) null else cachedItems

    /** Return single hero post for instant display on app entry (null if expired). */
    fun getHeroPost(): Post? = if (isExpired()) null else heroPost

    fun setFirstPage(items: List<Post>) {
        cachedItems = items
        if (items.isNotEmpty()) {
            heroPost = items.first()
        }
        storedAtMs = Clock.System.now().toEpochMilliseconds()
    }

    fun clear() {
        cachedItems = null
        heroPost = null
        storedAtMs = null
    }
}
